import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Users, UserPlus, Clock, ArrowUpRight, Loader2, AlertTriangle, Sparkles } from '../../icons/wp-icons';
import { useAuth } from '../../context/AuthContext';
import { getPlanLimits } from '../../api/stripeApi';

const PLAN_NAMES = {
  STARTER: 'Starter',
  PROFESSIONAL: 'Professional',
  BUSINESS: 'Business'
};

const STATUS_LABELS = {
  TRIAL: 'Testphase',
  ACTIVE: 'Aktiv',
  SUSPENDED: 'Pausiert',
  CANCELLED: 'Gekündigt'
};

const UNLIMITED_THRESHOLD = 999999;
const isUnlimited = (max) => max >= UNLIMITED_THRESHOLD;

function PlanWidget() {
  const { user } = useAuth();
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLimits();
  }, []);

  const loadLimits = async () => {
    try {
      setLoading(true);
      const data = await getPlanLimits();
      setLimits(data);
    } catch (err) {
      console.error('Failed to load limits:', err);
      setError('Limits konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const organisation = user?.organisation;
  const planName = PLAN_NAMES[organisation?.plan] || organisation?.plan;
  const statusLabel = STATUS_LABELS[organisation?.planStatus] || organisation?.planStatus;

  // Trial-Tage berechnen
  const getTrialDaysRemaining = () => {
    if (organisation?.planStatus !== 'TRIAL' || !organisation?.trialEndsAt) return null;
    const now = new Date();
    const trialEnd = new Date(organisation.trialEndsAt);
    const diff = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const trialDays = getTrialDaysRemaining();

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-light rounded-lg shrink-0">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold">Ihr Abonnement</h2>
              <p className="text-xs sm:text-sm text-text-muted hidden sm:block">
                Aktueller Plan und Nutzung
              </p>
            </div>
          </div>
          <Link
            to="/einstellungen/abonnement"
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary border-2 border-primary text-white text-sm rounded-button font-medium hover:bg-primary-dark hover:border-primary-dark transition"
          >
            Verwalten
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Limit überschritten Warnung */}
        {limits && ((!isUnlimited(limits.limits.kunden.max) && limits.limits.kunden.used > limits.limits.kunden.max) || limits.limits.users.used > limits.limits.users.max) && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800 text-sm">Limit überschritten</h4>
              <p className="text-xs text-red-700 mt-1">
                Ihre Nutzung übersteigt die Plan-Limits. Neue Einträge können nicht erstellt werden.
              </p>
              <Link
                to="/einstellungen/abonnement"
                className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-red-700 hover:text-red-800"
              >
                Plan upgraden
                <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {/* Plan Info */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="text-sm text-text-muted">Plan</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-semibold">{planName}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                organisation?.planStatus === 'TRIAL'
                  ? 'bg-orange-100 text-orange-700'
                  : organisation?.planStatus === 'ACTIVE'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
              }`}>
                {statusLabel}
              </span>
            </div>
          </div>

          {/* Trial Info */}
          {trialDays !== null && (
            <div className="text-right">
              <div className="flex items-center gap-1 text-orange-600">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {trialDays === 0 ? 'Heute' : `${trialDays} Tag${trialDays !== 1 ? 'e' : ''}`}
                </span>
              </div>
              <span className="text-xs text-text-muted">verbleibend</span>
            </div>
          )}
        </div>

        {/* Limits */}
        {limits && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Kunden-Limit */}
            <div className={`p-4 border rounded-lg ${!isUnlimited(limits.limits.kunden.max) && limits.limits.kunden.used > limits.limits.kunden.max ? 'border-red-300 bg-red-50' : 'border-border'}`}>
              <div className="flex items-center gap-2 text-text-muted mb-2">
                <Users className="w-4 h-4" />
                <span className="text-sm">Kunden</span>
              </div>
              <div className="flex items-end gap-2">
                <span className={`text-2xl font-bold ${!isUnlimited(limits.limits.kunden.max) && limits.limits.kunden.used > limits.limits.kunden.max ? 'text-red-600' : ''}`}>
                  {limits.limits.kunden.used}
                </span>
                <span className="text-text-muted mb-1">/ {isUnlimited(limits.limits.kunden.max) ? '∞' : limits.limits.kunden.max}</span>
              </div>
              {!isUnlimited(limits.limits.kunden.max) && (
                <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      limits.limits.kunden.used > limits.limits.kunden.max
                        ? 'bg-red-500'
                        : limits.limits.kunden.remaining === 0
                          ? 'bg-red-500'
                          : limits.limits.kunden.remaining <= 5
                            ? 'bg-orange-500'
                            : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(100, (limits.limits.kunden.used / limits.limits.kunden.max) * 100)}%` }}
                  />
                </div>
              )}
              {isUnlimited(limits.limits.kunden.max) ? (
                <p className="text-xs text-text-muted mt-1">Unbegrenzt</p>
              ) : limits.limits.kunden.used > limits.limits.kunden.max ? (
                <p className="text-xs text-red-600 mt-1 font-medium">Limit überschritten!</p>
              ) : limits.limits.kunden.remaining === 0 ? (
                <p className="text-xs text-red-600 mt-1">Limit erreicht</p>
              ) : null}
            </div>

            {/* Benutzer-Limit (ohne Inhaber) */}
            <div className={`p-4 border rounded-lg ${limits.limits.users.used > limits.limits.users.max ? 'border-red-300 bg-red-50' : 'border-border'}`}>
              <div className="flex items-center gap-2 text-text-muted mb-2">
                <UserPlus className="w-4 h-4" />
                <span className="text-sm">Mitarbeiter</span>
                <span className="text-xs text-gray-400">(+Inhaber)</span>
              </div>
              <div className="flex items-end gap-2">
                <span className={`text-2xl font-bold ${limits.limits.users.used > limits.limits.users.max ? 'text-red-600' : ''}`}>
                  {limits.limits.users.used}
                </span>
                <span className="text-text-muted mb-1">/ {limits.limits.users.max}</span>
              </div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    limits.limits.users.used > limits.limits.users.max
                      ? 'bg-red-500'
                      : limits.limits.users.remaining === 0
                        ? 'bg-red-500'
                        : limits.limits.users.remaining <= 1
                          ? 'bg-orange-500'
                          : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, (limits.limits.users.used / limits.limits.users.max) * 100)}%` }}
                />
              </div>
              {limits.limits.users.used > limits.limits.users.max ? (
                <p className="text-xs text-red-600 mt-1 font-medium">Limit überschritten!</p>
              ) : limits.limits.users.remaining === 0 ? (
                <p className="text-xs text-red-600 mt-1">Limit erreicht</p>
              ) : null}
            </div>

            {/* KI-Anfragen-Limit */}
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-center gap-2 text-text-muted mb-2">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">KI-Anfragen</span>
                <span className="text-xs text-gray-400">/Monat</span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-bold">{limits.limits.kiAnfragen.used}</span>
                <span className="text-text-muted mb-1">/ {limits.limits.kiAnfragen.max}</span>
              </div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    limits.limits.kiAnfragen.remaining === 0
                      ? 'bg-red-500'
                      : limits.limits.kiAnfragen.remaining <= 5
                        ? 'bg-orange-500'
                        : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, (limits.limits.kiAnfragen.used / limits.limits.kiAnfragen.max) * 100)}%` }}
                />
              </div>
              {limits.limits.kiAnfragen.remaining === 0 && (
                <p className="text-xs text-red-600 mt-1">Limit erreicht</p>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

export default PlanWidget;
